export const GROK_RUNTIME_ENGINE = "grok_build";
export const GROK_RUNTIME_PROVIDER = "nous";
export const GROK_RUNTIME_MODEL = "dynamic-free-catalog";
export const GROK_BUILD_SOURCE_COMMIT =
  "98c3b2438aa922fbbe6178a5c0a4c48f85edc8ce";
export const GROK_BUILD_SOURCE_REVISION =
  "124d85bc5dc6e7805560215fcc6d5413944920e1";
export const GROK_BUILD_ACP_CLIENT_VERSION = "1.2.1";

const RECEIPT_HEADERS = {
  engine: "X-Fintheon-Engine",
  provider: "X-Fintheon-Provider",
  model: "X-Fintheon-Model",
  modelAccess: "X-Fintheon-Model-Access",
  conversationId: "X-Conversation-Id",
  requestId: "X-Request-Id",
  taskId: "X-Agentic-Task-Id",
} as const;

const RECEIPT_ID_PATTERN = /^[a-z0-9][a-z0-9._:-]{0,159}$/i;
const SHA256_PATTERN = /^[a-f0-9]{64}$/;
const TARGET_PATTERN = /^[a-z0-9][a-z0-9._-]{0,127}$/i;
const FAILURE_CODE_PATTERN = /^[a-z0-9][a-z0-9._-]{0,79}$/;
const RUNTIME_STATES = new Set(["ready", "degraded", "unavailable"]);
const ARTIFACT_STATES = new Set(["verified", "pending", "missing", "invalid"]);
const POOL_STATES = new Set(["ready", "degraded", "unavailable"]);

export interface GrokRuntimeReceipt {
  engine: typeof GROK_RUNTIME_ENGINE;
  provider: string;
  model: string;
  modelAccess: "verified-free" | "verified-entitled";
  conversationId: string;
  requestId: string;
  taskId: string;
}

export type GrokRuntimeReceiptResult =
  | { ok: true; receipt: GrokRuntimeReceipt }
  | {
      ok: false;
      code:
        | "engine_mismatch"
        | "provider_mismatch"
        | "model_mismatch"
        | "identity_missing";
      message: string;
    };

export interface GrokRuntimeDiagnostics {
  available: boolean;
  state: "ready" | "degraded" | "unavailable";
  detail: string;
  artifactVerified: boolean;
  pool: {
    status: "ready" | "degraded" | "unavailable";
    capacity: number;
    resident: number;
    queued: number;
  };
  recoverable: boolean;
  failureCode: string | null;
}

interface HeaderReader {
  get(name: string): string | null;
}

export function readGrokRuntimeReceipt(
  headers: HeaderReader,
): GrokRuntimeReceiptResult {
  const engine = headers.get(RECEIPT_HEADERS.engine);
  if (engine !== GROK_RUNTIME_ENGINE) {
    return mismatch(
      "engine_mismatch",
      "Fintheon could not verify the chat runtime. Try again in a moment.",
    );
  }

  const provider = headers.get(RECEIPT_HEADERS.provider);
  if (!provider || !RECEIPT_ID_PATTERN.test(provider)) {
    return mismatch(
      "provider_mismatch",
      "Fintheon could not verify the selected provider for this durable run.",
    );
  }

  const model = headers.get(RECEIPT_HEADERS.model);
  if (!model || !RECEIPT_ID_PATTERN.test(model)) {
    return mismatch(
      "model_mismatch",
      "Fintheon could not verify the selected model for this durable run.",
    );
  }
  const modelAccess = headers.get(RECEIPT_HEADERS.modelAccess);
  if (modelAccess !== "verified-free" && modelAccess !== "verified-entitled") {
    return mismatch(
      "model_mismatch",
      "Fintheon could not verify this model's access policy.",
    );
  }

  const conversationId = readReceiptId(
    headers.get(RECEIPT_HEADERS.conversationId),
  );
  const requestId = readReceiptId(headers.get(RECEIPT_HEADERS.requestId));
  const taskId = readReceiptId(headers.get(RECEIPT_HEADERS.taskId));
  if (!conversationId || !requestId || !taskId) {
    return mismatch(
      "identity_missing",
      "Fintheon could not verify this durable run. Your message was not accepted; try again.",
    );
  }

  return {
    ok: true,
    receipt: {
      engine,
      provider,
      model,
      modelAccess,
      conversationId,
      requestId,
      taskId,
    },
  };
}

export function verifyGrokRuntimeSelection(
  receipt: GrokRuntimeReceipt,
  selection: { provider: string; model?: string | null },
): { ok: true } | { ok: false; message: string } {
  if (receipt.provider !== selection.provider) {
    return {
      ok: false,
      message:
        "Fintheon returned a different provider than the one selected. The response was stopped.",
    };
  }
  if (selection.model === GROK_RUNTIME_MODEL) {
    return receipt.modelAccess === "verified-free"
      ? { ok: true }
      : {
          ok: false,
          message:
            "Fintheon could not verify that the selected automatic route used a free model.",
        };
  }
  if (selection.model && receipt.model !== selection.model) {
    return {
      ok: false,
      message:
        "Fintheon returned a different model than the one selected. The response was stopped.",
    };
  }
  return { ok: true };
}

export function readGrokRuntimeDiagnostics(
  payload: unknown,
): GrokRuntimeDiagnostics | null {
  const root = readRecord(payload);
  const runtime = readRecord(root?.interactive_runtime);
  if (
    runtime?.engine !== GROK_RUNTIME_ENGINE ||
    typeof runtime.status !== "string" ||
    !RUNTIME_STATES.has(runtime.status) ||
    typeof runtime.recoverable !== "boolean"
  ) {
    return null;
  }

  const revision = readRecord(runtime.revision);
  if (
    revision?.sourceCommit !== GROK_BUILD_SOURCE_COMMIT ||
    revision.sourceRevision !== GROK_BUILD_SOURCE_REVISION ||
    revision.acpClientVersion !== GROK_BUILD_ACP_CLIENT_VERSION ||
    typeof revision.targetTriple !== "string" ||
    !TARGET_PATTERN.test(revision.targetTriple) ||
    typeof revision.executableSha256 !== "string" ||
    !SHA256_PATTERN.test(revision.executableSha256)
  ) {
    return null;
  }

  const artifact = readRecord(runtime.artifact);
  if (
    typeof artifact?.status !== "string" ||
    !ARTIFACT_STATES.has(artifact.status) ||
    typeof artifact.verified !== "boolean" ||
    (artifact.status === "verified") !== artifact.verified
  ) {
    return null;
  }

  const pool = readRecord(runtime.pool);
  if (
    typeof pool?.status !== "string" ||
    !POOL_STATES.has(pool.status) ||
    !isCount(pool.capacity) ||
    !isCount(pool.resident) ||
    !isCount(pool.queued) ||
    pool.resident > pool.capacity
  ) {
    return null;
  }

  const ready =
    runtime.status === "ready" &&
    artifact.status === "verified" &&
    artifact.verified &&
    pool.status === "ready";
  if (runtime.status === "ready" && !ready) return null;

  const failureCode =
    runtime.lastFailureCode == null
      ? null
      : typeof runtime.lastFailureCode === "string" &&
          FAILURE_CODE_PATTERN.test(runtime.lastFailureCode)
        ? runtime.lastFailureCode
        : "runtime_status_invalid";

  return {
    available: ready,
    state: runtime.status as GrokRuntimeDiagnostics["state"],
    detail: ready
      ? "Grok Build runtime ready"
      : runtime.recoverable
        ? "Runtime recovering; durable runs can resume."
        : "Runtime unavailable.",
    artifactVerified: artifact.verified,
    pool: {
      status: pool.status as GrokRuntimeDiagnostics["pool"]["status"],
      capacity: pool.capacity,
      resident: pool.resident,
      queued: pool.queued,
    },
    recoverable: runtime.recoverable,
    failureCode,
  };
}

export function grokRuntimeErrorMessage(status: number): string {
  if (status === 401 || status === 403) {
    return "Reconnect the selected provider in Settings.";
  }
  if (status === 409 || status === 429) {
    return "Fintheon is at capacity. Your conversation is safe; try again in a moment.";
  }
  if (status === 502 || status === 503 || status === 504) {
    return "The Fintheon runtime is recovering. Retry this run in a moment.";
  }
  return `Chat request failed (${status}).`;
}

function mismatch(
  code: Exclude<GrokRuntimeReceiptResult, { ok: true }>["code"],
  message: string,
): GrokRuntimeReceiptResult {
  return { ok: false, code, message };
}

function readReceiptId(value: string | null): string | null {
  return value && RECEIPT_ID_PATTERN.test(value) ? value : null;
}

function readRecord(value: unknown): Record<string, any> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, any>)
    : null;
}

function isCount(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 0;
}
